import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  FileText, Upload, Trash2, Search, Loader2, X, Download,
  File, FileImage, FileVideo, FileAudio, FileArchive
} from 'lucide-react';
import { teacherPanelAPI } from '../../services/api';

const MATERIAL_TYPES = [
  { value: 'lecture', label: "Ma'ruza" },
  { value: 'practice', label: 'Amaliyot' },
  { value: 'lab', label: 'Laboratoriya' },
  { value: 'test', label: 'Test' },
  { value: 'other', label: 'Boshqa' }
];

const getFileIcon = (extension) => {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const videoExts = ['mp4', 'avi', 'mov', 'mkv', 'webm'];
  const audioExts = ['mp3', 'wav', 'ogg', 'flac'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
  
  if (imageExts.includes(extension)) return FileImage;
  if (videoExts.includes(extension)) return FileVideo;
  if (audioExts.includes(extension)) return FileAudio;
  if (archiveExts.includes(extension)) return FileArchive;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const TeacherMaterials = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState(searchParams.get('subject') || '');
  const [filterType, setFilterType] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    material_type: 'lecture',
    file: null
  });

  useEffect(() => {
    fetchMaterials();
  }, [filterSubject]);

  // URL dan subject o'zgarganda filterni yangilash
  useEffect(() => {
    const subjectFromUrl = searchParams.get('subject');
    if (subjectFromUrl) {
      setFilterSubject(subjectFromUrl);
    }
  }, [searchParams]);

  // Filter o'zgarganda URL ni yangilash
  const handleFilterChange = (subjectId) => {
    setFilterSubject(subjectId);
    if (subjectId) {
      setSearchParams({ subject: subjectId });
    } else {
      setSearchParams({});
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await teacherPanelAPI.getMaterials(filterSubject);
      setMaterials(response.data.materials || []);
      setSubjects(response.data.subjects || []);
    } catch (error) {
      console.error('Materiallarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file,
        title: prev.title || file.name.split('.').slice(0, -1).join('.')
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.subject) {
      alert('Fayl va fanni tanlang');
      return;
    }
    
    setUploading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('subject', formData.subject);
      data.append('material_type', formData.material_type);
      data.append('file', formData.file);
      
      await teacherPanelAPI.uploadMaterial(data);
      fetchMaterials();
      closeModal();
    } catch (error) {
      console.error('Yuklashda xato:', error);
      alert(error.response?.data?.errors?.subject?.[0] || 'Xatolik yuz berdi');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Materialni o'chirishni tasdiqlaysizmi?")) {
      try {
        await teacherPanelAPI.deleteMaterial(id);
        fetchMaterials();
      } catch (error) {
        console.error("O'chirishda xato:", error);
      }
    }
  };

  const openModal = () => {
    setFormData({
      title: '',
      description: '',
      subject: filterSubject || subjects[0]?.id || '',
      material_type: 'lecture',
      file: null
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      title: '',
      description: '',
      subject: '',
      material_type: 'lecture',
      file: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredMaterials = materials.filter(m => {
    const matchSearch = m.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = !filterType || m.material_type === filterType;
    return matchSearch && matchType;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><FileText size={28} /> Materiallar</h1>
          <p>O'quv materiallari va resurslar</p>
        </div>
        <button className="btn btn-primary" onClick={openModal} disabled={subjects.length === 0}>
          <Upload size={20} /> Material yuklash
        </button>
      </header>

      {subjects.length === 0 ? (
        <div className="alert alert-warning">
          <p>Sizga hali fan biriktirilmagan. Admin bilan bog'laning.</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="filters-row">
            <div className="search-bar">
              <Search size={20} />
              <input 
                type="text" 
                placeholder="Materialni qidirish..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              className="filter-select"
              value={filterSubject}
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <option value="">Barcha fanlar</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select 
              className="filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Barcha turlar</option>
              {MATERIAL_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Materials List */}
          {filteredMaterials.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <p>Materiallar topilmadi</p>
              <button className="btn btn-primary" onClick={openModal}>
                <Upload size={20} /> Birinchi materialni yuklash
              </button>
            </div>
          ) : (
            <div className="materials-grid">
              {filteredMaterials.map(material => {
                const FileIcon = getFileIcon(material.file_extension);
                return (
                  <div key={material.id} className="material-card">
                    <div className="material-icon">
                      <FileIcon size={32} />
                      <span className="file-ext">.{material.file_extension}</span>
                    </div>
                    <div className="material-content">
                      <h3>{material.title}</h3>
                      <p className="material-subject">{material.subject_name}</p>
                      {material.description && (
                        <p className="material-desc">{material.description}</p>
                      )}
                      <div className="material-meta">
                        <span className="badge badge-info">{material.type_display}</span>
                        <span className="file-size">{formatFileSize(material.file_size)}</span>
                      </div>
                    </div>
                    <div className="material-actions">
                      <a 
                        href={material.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-icon download"
                        title="Yuklab olish"
                      >
                        <Download size={18} />
                      </a>
                      <button 
                        className="btn-icon delete"
                        onClick={() => handleDelete(material.id)}
                        title="O'chirish"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yangi material yuklash</h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Fan *</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                  >
                    <option value="">Fanni tanlang</option>
                    {subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Material turi</label>
                  <select
                    value={formData.material_type}
                    onChange={(e) => setFormData({...formData, material_type: e.target.value})}
                  >
                    {MATERIAL_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Nomi *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Material nomi"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Tavsif</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Qisqa tavsif..."
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Fayl *</label>
                <div className="file-upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    id="file-input"
                    required
                  />
                  <label htmlFor="file-input" className="file-upload-label">
                    {formData.file ? (
                      <div className="file-selected">
                        <File size={24} />
                        <span>{formData.file.name}</span>
                        <small>{formatFileSize(formData.file.size)}</small>
                      </div>
                    ) : (
                      <div className="file-placeholder">
                        <Upload size={32} />
                        <span>Faylni tanlang yoki bu yerga tashlang</span>
                        <small>PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, ZIP, va boshqalar</small>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="spinner" size={18} />
                      Yuklanmoqda...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Yuklash
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherMaterials;
